import { useState, useCallback } from "react";
import MembershipsService from "@/services/MembershipsService";

export default function useMembershipsData() {
  const defaultLimit = 5;
  const limitIncrement = 5;
  const defaultSortColumn = "created";
  const defaultSortDirection = "desc";

  // Data State
  const [data, setData] = useState([]);
  
  // UI State
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [searching, setSearching] = useState(false);
  const [error, setError] = useState(null);
  
  // Pagination State
  const [totalCount, setTotalCount] = useState(0);
  const [hasMore, setHasMore] = useState(false);
  const [limit, setLimit] = useState(defaultLimit);
  
  // Filter/Sort State
  const [sortColumn, setSortColumn] = useState(defaultSortColumn);
  const [sortDirection, setSortDirection] = useState(defaultSortDirection);
  const [searchQuery, setSearchQuery] = useState("");
  const [filters, setFilters] = useState({});

  /**
   * Load memberships data using the service
   */
  const loadData = useCallback(async () => {
    if (!loadingMore) setLoading(true);
    setError(null);

    // Use the MembershipsService to get memberships with filters
    const result = await MembershipsService.getMembershipsWithFilters({
      limit,
      sortColumn,
      sortDirection,
      filters: {
        searchText: searchQuery,
        ...filters
      }
    });
    
    if (result.error) {
      setError(result.error);
      setData([]);
      setTotalCount(0);
      setHasMore(false);
    } else {
      setData(result.data);
      setTotalCount(result.totalCount);
      setHasMore(result.hasMore);
    }

    setLoading(false);
    setLoadingMore(false);
  }, [searchQuery, limit, sortColumn, sortDirection, filters, loadingMore]);

  /**
   * Load more memberships for pagination
   */
  const handleLoadMore = useCallback(async () => {
    if (loadingMore || !hasMore) return;
    
    setLoadingMore(true);
    setError(null);

    const newLimit = limit + limitIncrement;
    
    // Use the service to get memberships with the new limit
    const result = await MembershipsService.getMembershipsWithFilters({
      limit: newLimit,
      sortColumn,
      sortDirection,
      filters: {
        searchText: searchQuery,
        ...filters
      }
    });
    
    if (result.error) {
      setError(result.error);
    } else {
      setData(result.data);
      setLimit(newLimit);
      setHasMore(result.hasMore);
    }

    setLoadingMore(false);
  }, [searchQuery, limit, sortColumn, sortDirection, filters, hasMore, loadingMore]);

  /**
   * Update sorting parameters and reload data
   */
  const setSorting = useCallback((column, direction) => {
    setSortColumn(column);
    setSortDirection(direction);
  }, []);

  /**
   * Handle search input changes
   */
  const handleSearch = useCallback((query) => {
    setSearchQuery(query);
  }, []);

  /**
   * Update filters and reload data
   */
  const updateFilters = useCallback((newFilters) => {
    setFilters(prevFilters => ({
      ...prevFilters,
      ...newFilters
    }));
  }, []);

  /**
   * Clear all filters
   */
  const clearFilters = useCallback(() => {
    setFilters({});
    setSearchQuery("");
    setLimit(defaultLimit);
  }, []);

  /**
   * Reset all state to defaults
   */
  const resetState = useCallback(() => {
    setData([]);
    setLoading(true);
    setLoadingMore(false);
    setSearching(false);
    setError(null);
    setTotalCount(0);
    setHasMore(false);
    setLimit(defaultLimit);
    setSortColumn(defaultSortColumn);
    setSortDirection(defaultSortDirection);
    setSearchQuery("");
    setFilters({});
  }, []);

  /**
   * Refresh data (reload current state)
   */
  const refreshData = useCallback(() => {
    loadData();
  }, [loadData]);

  return {
    // Data
    data,
    
    // UI State
    loading,
    loadingMore,
    searching,
    error,
    
    // Pagination
    totalCount,
    hasMore,
    
    // Filter/Sort State
    sortColumn,
    sortDirection,
    searchQuery,
    filters,
    
    // Actions
    loadData,
    handleLoadMore,
    setSorting,
    handleSearch,
    updateFilters,
    clearFilters,
    resetState,
    refreshData,
    setError
  };
}
